"use client";

import { Mail, Phone, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
const packageOptions = [{
  value: "Standard Inspection",
  label: "Standard Inspection",
  description: "Comprehensive property assessment"
}, {
  value: "Energy Audit",
  label: "Energy Audit",
  description: "Efficiency and savings analysis"
}, {
  value: "Safety Check",
  label: "Safety Check",
  description: "Critical hazard identification"
}];
const budgetOptions = ["Under $300", "$300 — $500", "$500 — $800", "$800 — $1,200", "$1,200 and above"];
function ConsultationFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-b border-border py-16 text-center">
        <p className="font-serif text-3xl italic text-primary mb-4">Request Sent</p>
        <p className="font-default text-base text-muted-foreground mb-8 max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background px-8 py-6 tracking-widest text-xs uppercase">
          New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-16">
      {}
      <div className="space-y-8">
        <p className="font-default text-[11px] uppercase tracking-[0.25em] text-primary border-b border-border pb-3">
          Client Info
        </p>

        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                Full Name <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="h-12 px-0 border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-serif placeholder:text-muted-foreground/50 placeholder:font-serif placeholder:italic" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid md:grid-cols-2 gap-8">
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem className="gap-3">
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  Email <span className="text-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@email.com" {...field} className="h-12 px-0 border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-serif placeholder:text-muted-foreground/50 placeholder:font-serif placeholder:italic" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem className="gap-3">
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  Phone <span className="text-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(256) 555-0123" {...field} className="h-12 px-0 border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-serif placeholder:text-muted-foreground/50 placeholder:font-serif placeholder:italic" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-8">
        <p className="font-default text-[11px] uppercase tracking-[0.25em] text-primary border-b border-border pb-3">
          Property Details
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <FormField control={control} name="inspection_date" render={({
          field
        }) => <FormItem className="gap-3">
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  Date <span className="text-primary">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="date" {...field} className="h-12 px-0 border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-serif" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="property_address" render={({
          field
        }) => <FormItem className="gap-3">
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  Address
                </FormLabel>
                <FormControl>
                  <Input placeholder="123 Main St, City" {...field} className="h-12 px-0 border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-serif placeholder:text-muted-foreground/50 placeholder:font-serif placeholder:italic" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-8">
        <p className="font-default text-[11px] uppercase tracking-[0.25em] text-primary border-b border-border pb-3">
          Service Scope
        </p>

        <FormField control={control} name="service_type" render={({
        field
      }) => <FormItem className="gap-4">
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                Service <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value} className="gap-0 divide-y divide-border">
                  {packageOptions.map((opt, i) => <label key={opt.value} htmlFor={`pkg-${i}`} data-index={i} className="flex items-start gap-4 py-5 cursor-pointer group">
                      <RadioGroupItem value={opt.value} id={`pkg-${i}`} className="mt-1.5 size-4 border-input data-[state=checked]:border-primary" />
                      <div className="flex-1">
                        <p className="font-serif text-xl text-foreground">{opt.label}</p>
                        <p className="font-default text-sm text-muted-foreground italic mt-0.5">
                          {opt.description}
                        </p>
                      </div>
                    </label>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget_range" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                Budget
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12 px-0 border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-serif data-[placeholder]:text-muted-foreground/50 data-[placeholder]:italic w-full">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="border-border">
                  {budgetOptions.map((b, i) => <SelectItem key={b} value={b} data-index={i} className="font-serif text-base">
                      {b}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="notes" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                Additional Notes
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Specific concerns or details..." rows={5} {...field} className="px-0 py-3 border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-serif resize-none placeholder:text-muted-foreground/50 placeholder:font-serif placeholder:italic" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="flex items-start gap-3 pt-4">
        <Checkbox id="updates" className="mt-1 size-4 border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
        <label htmlFor="updates" className="font-default text-sm text-muted-foreground cursor-pointer leading-relaxed">
          Send me maintenance tips and safety alerts.
        </label>
      </div>

      {submitError && <div className="border-l-2 border-destructive bg-destructive/5 px-5 py-4">
          <p className="font-default text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-6">
        <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 px-12 py-7 text-xs uppercase tracking-[0.3em] font-default font-medium w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending
            </> : "Submit Request"}
        </Button>
        <p className="font-serif italic text-base text-muted-foreground">
          We will confirm your booking soon.
        </p>
      </div>
    </Form>;
}
export default function InspectionRequest() {
  return <section data-section-id="559" id="getstartedinspectionform" className="bg-background text-foreground py-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-20">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 space-y-10">
              <div>
                <p className="font-default text-[11px] uppercase tracking-[0.3em] text-primary mb-8">
                  Inspection Request
                </p>
                <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-foreground">
                  Schedule Your{" "}
                  <span className="italic">Assessment</span>
                </h1>
              </div>

              <div className="w-12 h-px bg-border" />

              <p className="font-default text-base leading-relaxed text-muted-foreground">
                Secure your property with a professional inspection. Our team provides detailed
                reports and actionable insights to ensure your home remains safe, efficient,
                and well-maintained for years to come.
              </p>

              <p className="font-serif italic text-lg text-foreground">
                Booking confirmed within 24 hours.
              </p>

              <div className="pt-6 border-t border-border space-y-4">
                <p className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-5">
                  Contact Us
                </p>
                <Link to="tel:+18334209973" className="flex items-center gap-3 group">
                  <Phone className="size-4 text-primary" strokeWidth={1.25} />
                  <span className="font-serif text-lg text-foreground group-hover:text-primary transition-colors">
                    (833) 420-9973
                  </span>
                </Link>
                <Link to="mailto:solutions@rocketcitywaterwizards.com" className="flex items-center gap-3 group">
                  <Mail className="size-4 text-primary" strokeWidth={1.25} />
                  <span className="font-serif text-lg text-foreground group-hover:text-primary transition-colors">
                    solutions@rocketcitywaterwizards.com
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <FormProvider formKey="get_started_form" formId="get_started_form" sectionName="getstartedinspectionform" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            inspection_date: "",
            property_address: "",
            service_type: "",
            budget_range: "",
            notes: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            inspection_date: {
              required: true
            },
            service_type: {
              required: true
            }
          }} submitText="Submit Request" successMessage="Your request is received. We will contact you shortly to finalize the details." errorMessage="Submission failed. Please check your details or call us directly.">
              <ConsultationFormFields />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}